import React, { useState, useRef, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, ProgressBar, Alert, Badge, Tab, Nav } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import ShareModal from '../components/ShareModal';
import axios from 'axios';

const Record = () => {
  const [selectedPlatform, setSelectedPlatform] = useState('teams');
  const [meetingId, setMeetingId] = useState('');
  const [meetingPassword, setMeetingPassword] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [recordingTranscript, setRecordingTranscript] = useState('');
  const [activeStep, setActiveStep] = useState(0); // 0: connect, 1: record, 2: review, 3: processing
  const [meetingTitle, setMeetingTitle] = useState('');
  const [processingProgress, setProcessingProgress] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');
  const [showShareModal, setShowShareModal] = useState(false);
  const [recordingData, setRecordingData] = useState(null);
  const [audioSource, setAudioSource] = useState('mic'); // 'mic', 'speaker', 'both'
  const [recordingPermissionGranted, setRecordingPermissionGranted] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [recordingFormat, setRecordingFormat] = useState('video'); // 'video', 'audio-only'
  const [recordingQuality, setRecordingQuality] = useState('standard'); // 'standard', 'high'
  
  // New refs for recording functionality
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const timerRef = useRef(null);
  const audioContextRef = useRef(null);
  const navigate = useNavigate();

  // Clean up resources on component unmount
  useEffect(() => {
    return () => {
      stopAllMediaTracks();
      clearInterval(timerRef.current);
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close();
      }
    };
  }, []);

  const stopAllMediaTracks = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  };

  // Format time for display (MM:SS)
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle platform selection
  const handlePlatformSelect = (platform) => {
    setSelectedPlatform(platform);
  };

  // Handle audio source selection
  const handleAudioSourceChange = (source) => {
    setAudioSource(source);
  };

  // Handle recording format selection
  const handleRecordingFormatChange = (format) => {
    setRecordingFormat(format);
  };

  // Handle recording quality selection
  const handleRecordingQualityChange = (quality) => {
    setRecordingQuality(quality);
  };

  // Request screen and audio recording permissions
  const requestPermissions = async () => {
    try {
      setLoadingMessage('Requesting recording permissions...');
      setErrorMessage('');
      
      // Detect Safari browser - screen recording not fully supported
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      if (isSafari && recordingFormat !== 'audio-only') {
        setErrorMessage('Screen recording is not fully supported in Safari. Please use Chrome or Firefox, or switch to Audio Only mode.');
        setLoadingMessage('');
        return;
      }
      
      // Audio constraints based on selection
      let audioConstraints = {};
      
      if (audioSource === 'mic') {
        audioConstraints = { audio: true };
      } else if (audioSource === 'speaker') {
        // System audio requires getDisplayMedia with audio option
        audioConstraints = {}; // Will be handled with display capture
      } else if (audioSource === 'both') {
        audioConstraints = { audio: true }; // Mic audio, system will be handled with display
      }
      
      // Screen capture constraints - customize for standalone mode
      const displayMediaOptions = {
        video: {
          cursor: 'always',
          displaySurface: selectedPlatform === 'standalone' ? 'monitor' : 'browser',
        },
        // For speaker audio, we need to request it with getDisplayMedia
        audio: audioSource !== 'mic',
        selfBrowserSurface: 'include'
      };
      
      // Show helpful message for standalone mode
      if (selectedPlatform === 'standalone') {
        setLoadingMessage('Select what you want to record (entire screen, window, or browser tab)...');
      }
      
      // Get display media (screen)
      const displayStream = await navigator.mediaDevices.getDisplayMedia(displayMediaOptions);
      
      let combinedStream;
      
      // If we need microphone audio, get it and combine with display stream
      if (audioSource === 'mic' || audioSource === 'both') {
        const micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
        
        // Create new combined stream with all tracks
        const allTracks = [
          ...displayStream.getVideoTracks(),
          ...(audioSource === 'mic' ? micStream.getAudioTracks() : []),
          ...(audioSource !== 'mic' ? displayStream.getAudioTracks() : [])
        ];
        
        combinedStream = new MediaStream(allTracks);
      } else {
        combinedStream = displayStream;
      }
      
      streamRef.current = combinedStream;
      setRecordingPermissionGranted(true);
      setLoadingMessage('');
      
      // Move to next step - ready to record
      setActiveStep(1);
      
      // Auto-generate meeting title if not provided
      if (!meetingTitle) {
        if (selectedPlatform === 'standalone') {
          setMeetingTitle(`Standalone Recording - ${new Date().toLocaleDateString()}`);
        } else {
          const platformName = selectedPlatform === 'teams' ? 'Teams' : 
                             selectedPlatform === 'zoom' ? 'Zoom' : 'Google Meet';
          setMeetingTitle(`${platformName} Meeting - ${new Date().toLocaleDateString()}`);
        }
      }
      
      return true;
    } catch (error) {
      console.error('Error requesting permissions:', error);
      setErrorMessage(`Couldn't access recording permissions: ${error.message}`);
      setLoadingMessage('');
      return false;
    }
  };

  // Handle connecting to meeting
  const handleConnect = async () => {
    if (selectedPlatform === 'standalone') {
      // For standalone mode, we don't need meeting ID or password
      const permissionsGranted = await requestPermissions();
      if (!permissionsGranted) {
        return;
      }
    } else {
      if (!meetingId) {
        setErrorMessage('Please enter a meeting ID or link');
        return;
      }
      
      const permissionsGranted = await requestPermissions();
      if (!permissionsGranted) {
        return;
      }
    }
  };

  // Handle recording start/stop
  const toggleRecording = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };
  
  // Start recording
  const startRecording = () => {
    try {
      // Reset recording data
      recordedChunksRef.current = [];
      setRecordingTime(0);
      setRecordingTranscript('');
      
      // Determine media recorder options based on format and quality
      let options = {};
      
      // Detect browser for Safari-specific handling
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      
      // Helper function to get supported MIME type
      const getSupportedMimeType = (preferredTypes) => {
        for (const type of preferredTypes) {
          if (MediaRecorder.isTypeSupported(type)) {
            return type;
          }
        }
        return null; // Will use browser default
      };
      
      if (recordingFormat === 'audio-only' && selectedPlatform === 'standalone') {
        // Audio-only recording - try Safari-compatible formats first if Safari
        const audioTypes = isSafari 
          ? ['audio/mp4', 'audio/webm;codecs=opus', 'audio/webm']
          : ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'];
        
        const supportedType = getSupportedMimeType(audioTypes);
        if (supportedType) {
          options = { mimeType: supportedType };
        }
        console.log('Audio recording MIME type:', options.mimeType || 'browser default');
        
        // Create a new stream with only audio tracks
        const audioTracks = streamRef.current.getAudioTracks();
        if (audioTracks.length === 0) {
          throw new Error('No audio tracks available for audio-only recording');
        }
        
        // Create a new stream with only audio tracks
        const audioStream = new MediaStream(audioTracks);
        mediaRecorderRef.current = new MediaRecorder(audioStream, options);
      } else {
        // Video recording with quality options
        const videoTypes = isSafari
          ? ['video/mp4', 'video/webm;codecs=vp9,opus', 'video/webm']
          : ['video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm', 'video/mp4'];
        
        const supportedType = getSupportedMimeType(videoTypes);
        
        if (recordingQuality === 'high' && selectedPlatform === 'standalone') {
          // High quality video
          options = { 
            mimeType: supportedType,
            videoBitsPerSecond: 5000000,  // 5 Mbps
            audioBitsPerSecond: 128000    // 128 kbps
          };
        } else {
          // Standard quality video
          options = supportedType ? { mimeType: supportedType } : {};
        }
        console.log('Video recording MIME type:', options.mimeType || 'browser default');
        
        mediaRecorderRef.current = new MediaRecorder(streamRef.current, options);
      }
      
      // Handle data available event
      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          recordedChunksRef.current.push(event.data);
        }
      };
      
      // Handle recording stop - capture the actual MIME type used
      const actualMimeType = mediaRecorderRef.current.mimeType || options.mimeType || 
        (recordingFormat === 'audio-only' ? 'audio/webm' : 'video/webm');
      
      mediaRecorderRef.current.onstop = () => {
        // Create a blob from recorded chunks using the actual MIME type
        const recordingBlob = new Blob(recordedChunksRef.current, {
          type: actualMimeType
        });
        
        // Create URL for the recording
        const recordingUrl = URL.createObjectURL(recordingBlob);
        
        setRecordingData({
          url: recordingUrl,
          blob: recordingBlob,
          type: actualMimeType,
          size: recordingBlob.size,
          format: recordingFormat,
          quality: recordingQuality
        });
        
        setActiveStep(2); // Move to review step
      };
      
      // Start recording
      mediaRecorderRef.current.start(1000); // Collect data every second
      
      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
      setIsRecording(true);
    } catch (error) {
      console.error('Error starting recording:', error);
      setErrorMessage(`Failed to start recording: ${error.message}`);
    }
  };
  
  // Stop recording
  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
      clearInterval(timerRef.current);
      setIsRecording(false);
    }
  };

  // Handle saving the recording
  const handleSaveRecording = async () => {
    try {
      setActiveStep(3); // Move to processing step
      setProcessingProgress(0);
      
      if (!recordingData || !recordingData.blob) {
        throw new Error('No recording data available');
      }
      
      // Create a FormData object to upload the recording
      const formData = new FormData();
      formData.append('recording', recordingData.blob, 'meeting-recording.webm');
      formData.append('title', meetingTitle);
      formData.append('platform', selectedPlatform);
      
      // Upload the recording
      const uploadResponse = await axios.post('/api/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        timeout: 120000, // 2 minutes for large files
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 50) / progressEvent.total);
          setProcessingProgress(percentCompleted);
        }
      });
      
      const { success, fileId } = uploadResponse.data;
      console.log(`Upload successful, got fileId: ${fileId}`);
      
      if (!success || !fileId) {
        throw new Error('Upload failed or returned invalid file ID');
      }
      
      // Create a new meeting with the uploaded recording
      const meetingResponse = await axios.post('/api/meetings', {
        title: meetingTitle,
        meetingDate: new Date().toISOString(),
        recordingId: fileId,
        attendees: ['You'],
        platform: selectedPlatform,
        isStandaloneRecording: selectedPlatform === 'standalone'
      });
      
      const { meeting } = meetingResponse.data;
      
      // Store meeting ID in session storage as a backup
      sessionStorage.setItem('lastMeetingId', meeting.id);
      
      // Set progress to 60% after meeting creation
      setProcessingProgress(60);
      
      console.log(`Starting to poll meeting with ID: ${meeting.id}`);

      // Track poll attempts to avoid infinite polling
      let pollAttempts = 0;
      const maxPollAttempts = 40; // About 2 minutes of polling

      // Poll the meeting status every 3 seconds until processing is complete
      const pollInterval = setInterval(async () => {
        try {
          pollAttempts++;
          console.log(`Polling status for meeting: ${meeting.id} (Attempt ${pollAttempts}/${maxPollAttempts})`);
          
          // If we've been polling too long, assume it's done
          if (pollAttempts >= maxPollAttempts) {
            console.log(`Max poll attempts reached, forcing navigation to summary/${meeting.id}`);
            clearInterval(pollInterval);
            setProcessingProgress(100);
            setTimeout(() => {
              navigate(`/summary/${meeting.id}`);
            }, 1000);
            return;
          }
          
          const statusResponse = await axios.get(`/api/meetings/${meeting.id}`);
          const { meeting: updatedMeeting } = statusResponse.data;
          
          console.log(`Meeting status: ${updatedMeeting.status}`);
          
          if (updatedMeeting.status === 'completed') {
            clearInterval(pollInterval);
            setProcessingProgress(100);
            
            console.log(`Processing complete, navigating to summary/${meeting.id}`);
            setTimeout(() => {
              navigate(`/summary/${meeting.id}`);
            }, 1000);
          } else if (updatedMeeting.status === 'error') {
            clearInterval(pollInterval);
            setErrorMessage(`Processing error: ${updatedMeeting.error || 'Unknown error'}`);
            setProcessingProgress(0);
          } else {
            // Increment progress gradually from 60 to 95%
            setProcessingProgress(prev => {
              // More aggressive progress increment as polling continues
              const increment = Math.ceil(pollAttempts / 5);
              return Math.min(prev + increment, 95);
            });
          }
        } catch (error) {
          console.error(`Error polling meeting status for ID ${meeting.id}:`, error);
          
          // If we can't poll the meeting for some reason, try again a few times then give up
          if (pollAttempts > 10) {
            clearInterval(pollInterval);
            
            // Try to navigate to the summary page anyway after enough attempts
            console.log(`Polling failed but proceeding to summary page for meeting ${meeting.id}`);
            setProcessingProgress(100);
            setTimeout(() => {
              navigate(`/summary/${meeting.id}`);
            }, 1000);
          }
        }
      }, 3000);
      
    } catch (error) {
      console.error('Error saving recording:', error);
      setErrorMessage(`Failed to save recording: ${error.message}`);
      setProcessingProgress(0);
    }
  };

  // Handle disconnect from meeting
  const handleDisconnect = () => {
    if (isRecording) {
      stopRecording(); // Stop recording if in progress
    }
    stopAllMediaTracks();
    setRecordingPermissionGranted(false);
    setActiveStep(0);
  };

  return (
    <Container fluid className="p-3 p-md-4">
      <Row className="mb-4">
        <Col>
          <h1 className="text-white">Live Meeting Recording</h1>
          <p className="text-white">Connect to your meeting, record it, and automatically get transcriptions and summaries</p>
        </Col>
      </Row>
      
      {errorMessage && (
        <Row className="mb-4">
          <Col>
            <Alert variant="danger" onClose={() => setErrorMessage('')} dismissible>
              {errorMessage}
            </Alert>
          </Col>
        </Row>
      )}
      
      {loadingMessage && (
        <Row className="mb-4">
          <Col>
            <Alert variant="info">
              <div className="d-flex align-items-center">
                <div className="spinner-border spinner-border-sm me-2" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                {loadingMessage}
              </div>
            </Alert>
          </Col>
        </Row>
      )}
      
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between mb-4">
            {['Connect', 'Record', 'Review', 'Processing'].map((step, index) => (
              <div key={index} className="text-center" style={{ flex: 1 }}>
                <div 
                  className={`
                    rounded-circle d-flex align-items-center justify-content-center mx-auto mb-2
                    ${index < activeStep ? 'bg-success' : (index === activeStep ? 'bg-primary' : 'bg-dark')}
                  `}
                  style={{ width: '36px', height: '36px' }}
                >
                  {index < activeStep ? (
                    <i className="bi bi-check text-white"></i>
                  ) : (
                    <span className="text-white">{index + 1}</span>
                  )}
                </div>
                <div className="text-white">
                  {step}
                </div>
              </div>
            ))}
          </div>
        </Col>
      </Row>
      
      <Row>
        <Col>
          <Card className="glass-card">
            <Card.Body className="p-4">
              {activeStep === 0 && (
                <div>
                  <h3 className="mb-4 text-white">Connect to Meeting</h3>
                  
                  <Alert variant="info" className="bg-dark border-primary mb-4">
                    <div className="d-flex align-items-center">
                      <i className="bi bi-lightbulb-fill text-primary me-2" style={{ fontSize: '1.2rem' }}></i>
                      <div>
                        <strong>Quick Tip:</strong> Want to record without joining a meeting?
                        <div className="mt-2">
                          <Button 
                            variant="primary" 
                            size="sm" 
                            className="me-2"
                            onClick={() => {
                              setMeetingTitle("Standalone Recording - " + new Date().toLocaleDateString());
                              setSelectedPlatform('standalone');
                              requestPermissions();
                            }}
                          >
                            <i className="bi bi-record-fill me-1"></i> Start Standalone Recording
                          </Button>
                        </div>
                      </div>
                    </div>
                  </Alert>
                  
                  <div className="mb-4">
                    <h5 className="text-white mb-3">Select Platform</h5>
                    <div className="d-flex justify-content-center mb-4">
                      <div className="btn-group w-100">
                        <Button 
                          variant={selectedPlatform === 'teams' ? 'primary' : 'outline-primary'} 
                          onClick={() => handlePlatformSelect('teams')}
                          className="py-2"
                        >
                          <i className="bi bi-microsoft-teams me-2"></i>
                          Microsoft Teams
                        </Button>
                        <Button 
                          variant={selectedPlatform === 'zoom' ? 'primary' : 'outline-primary'} 
                          onClick={() => handlePlatformSelect('zoom')}
                          className="py-2"
                        >
                          <i className="bi bi-camera-video me-2"></i>
                          Zoom
                        </Button>
                        <Button 
                          variant={selectedPlatform === 'google' ? 'primary' : 'outline-primary'} 
                          onClick={() => handlePlatformSelect('google')}
                          className="py-2"
                        >
                          <i className="bi bi-google me-2"></i>
                          Google Meet
                        </Button>
                        <Button 
                          variant={selectedPlatform === 'standalone' ? 'primary' : 'outline-primary'} 
                          onClick={() => handlePlatformSelect('standalone')}
                          className="py-2"
                        >
                          <i className="bi bi-record-circle me-2"></i>
                          Standalone
                        </Button>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <h5 className="text-white mb-3">Audio Source</h5>
                    <Form>
                      <div className="mb-3">
                        <Form.Check
                          type="radio"
                          id="mic-only"
                          name="audioSource"
                          label="Microphone only (your voice)"
                          className="text-white mb-2"
                          checked={audioSource === 'mic'}
                          onChange={() => handleAudioSourceChange('mic')}
                        />
                        <Form.Check
                          type="radio"
                          id="speaker-only"
                          name="audioSource"
                          label="System audio only (what you hear)"
                          className="text-white mb-2"
                          checked={audioSource === 'speaker'}
                          onChange={() => handleAudioSourceChange('speaker')}
                        />
                        <Form.Check
                          type="radio"
                          id="both-audio"
                          name="audioSource"
                          label="Both microphone and system audio (recommended)"
                          className="text-white"
                          checked={audioSource === 'both'}
                          onChange={() => handleAudioSourceChange('both')}
                        />
                      </div>
                      <Alert variant="info" className="bg-dark border-info">
                        <i className="bi bi-info-circle me-2"></i>
                        <strong>Tip:</strong> For Microsoft Teams meetings, "Both" works best for capturing 
                        everyone's voices along with any shared audio.
                      </Alert>
                    </Form>
                  </div>
                  
                  {selectedPlatform === 'standalone' && (
                    <div className="mb-4">
                      <h5 className="text-white mb-3">Recording Format</h5>
                      <Form>
                        <div className="mb-3">
                          <Form.Check
                            type="radio"
                            id="video-format"
                            name="recordingFormat"
                            label="Video (default)"
                            className="text-white mb-2"
                            checked={recordingFormat === 'video'}
                            onChange={() => handleRecordingFormatChange('video')}
                          />
                          <Form.Check
                            type="radio"
                            id="audio-only-format"
                            name="recordingFormat"
                            label="Audio only"
                            className="text-white"
                            checked={recordingFormat === 'audio-only'}
                            onChange={() => handleRecordingFormatChange('audio-only')}
                          />
                        </div>
                      </Form>
                    </div>
                  )}
                  
                  {selectedPlatform === 'standalone' && (
                    <div className="mb-4">
                      <h5 className="text-white mb-3">Recording Quality</h5>
                      <Form>
                        <div className="mb-3">
                          <Form.Check
                            type="radio"
                            id="standard-quality"
                            name="recordingQuality"
                            label="Standard (default)"
                            className="text-white mb-2"
                            checked={recordingQuality === 'standard'}
                            onChange={() => handleRecordingQualityChange('standard')}
                          />
                          <Form.Check
                            type="radio"
                            id="high-quality"
                            name="recordingQuality"
                            label="High"
                            className="text-white"
                            checked={recordingQuality === 'high'}
                            onChange={() => handleRecordingQualityChange('high')}
                          />
                        </div>
                      </Form>
                    </div>
                  )}
                  
                  {selectedPlatform === 'standalone' ? (
                    <div className="mb-4">
                      <h5 className="text-white mb-3">Recording Title (Optional)</h5>
                      <Form>
                        <Form.Group className="mb-3">
                          <Form.Control 
                            type="text" 
                            placeholder="Enter recording title" 
                            value={meetingTitle}
                            onChange={(e) => setMeetingTitle(e.target.value)}
                            className="bg-dark text-white border-secondary"
                          />
                          <Form.Text className="text-white">
                            If not provided, we'll automatically generate a title
                          </Form.Text>
                        </Form.Group>
                        
                        <div className="d-grid mt-4">
                          <Button variant="primary" onClick={handleConnect} size="lg">
                            Start Recording
                          </Button>
                        </div>
                      </Form>
                    </div>
                  ) : (
                    <div className="mb-4">
                      <h5 className="text-white mb-3">Meeting Details</h5>
                      <Form>
                        <Form.Group className="mb-3">
                          <Form.Label className="text-white">Meeting Title (Optional)</Form.Label>
                          <Form.Control 
                            type="text" 
                            placeholder="Enter meeting title" 
                            value={meetingTitle}
                            onChange={(e) => setMeetingTitle(e.target.value)}
                            className="bg-dark text-white border-secondary"
                          />
                          <Form.Text className="text-white">
                            If not provided, we'll automatically generate a title
                          </Form.Text>
                        </Form.Group>
                    
                        <Form.Group className="mb-3">
                          <Form.Label className="text-white">Meeting ID or Link*</Form.Label>
                          <Form.Control 
                            type="text" 
                            placeholder="Enter meeting ID or paste meeting link" 
                            value={meetingId}
                            onChange={(e) => setMeetingId(e.target.value)}
                            required
                            className="bg-dark text-white border-secondary"
                          />
                        </Form.Group>
                      
                        <Form.Group className="mb-4">
                          <Form.Label className="text-white">Meeting Password (if required)</Form.Label>
                          <Form.Control 
                            type="password" 
                            placeholder="Enter meeting password" 
                            value={meetingPassword}
                            onChange={(e) => setMeetingPassword(e.target.value)}
                            className="bg-dark text-white border-secondary"
                          />
                        </Form.Group>
                      
                        <div className="d-grid mt-4">
                          <Button variant="primary" onClick={handleConnect} size="lg">
                            Connect & Start Recording
                          </Button>
                        </div>
                      </Form>
                    </div>
                  )}
                </div>
              )}
              
              {activeStep === 1 && (
                <div>
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <h3 className="text-white mb-0">Record {selectedPlatform === 'standalone' ? 'Audio/Video' : 'Meeting'}</h3>
                    <Badge bg={isRecording ? 'danger' : 'success'} className="p-2">
                      {isRecording ? 'Recording' : 'Ready'}
                    </Badge>
                  </div>
                  
                  <Card className="glass-card mb-4">
                    <Card.Body>
                      <div className="d-flex align-items-center mb-3">
                        <div className="me-3 text-primary" style={{ fontSize: '2rem' }}>
                          <i className={
                            selectedPlatform === 'teams' ? 'bi bi-microsoft-teams' : 
                            selectedPlatform === 'zoom' ? 'bi bi-camera-video' : 
                            selectedPlatform === 'google' ? 'bi bi-google' : 
                            'bi bi-record-circle'
                          }></i>
                        </div>
                        <div>
                          <h5 className="mb-1 text-white">
                            {selectedPlatform === 'teams' ? 'Microsoft Teams Meeting' : 
                             selectedPlatform === 'zoom' ? 'Zoom Meeting' : 
                             selectedPlatform === 'google' ? 'Google Meet Meeting' : 
                             'Standalone Recording'}
                          </h5>
                          <p className="mb-0 text-white">Audio: {audioSource === 'mic' ? 'Microphone only' : 
                                                           audioSource === 'speaker' ? 'System audio only' : 'Microphone + System audio'}</p>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                  
                  {selectedPlatform !== 'standalone' && (
                    <Alert variant="warning" className="mb-4">
                      <i className="bi bi-exclamation-triangle me-2"></i>
                      <strong>Important:</strong> Make sure your {selectedPlatform === 'teams' ? 'Teams' : selectedPlatform === 'zoom' ? 'Zoom' : 'Google Meet'} meeting is open in the browser before starting 
                      the recording. When prompted, select "Share Tab" and choose the tab with your meeting.
                    </Alert>
                  )}
                  
                  {selectedPlatform === 'standalone' && (
                    <Alert variant="info" className="mb-4">
                      <i className="bi bi-info-circle me-2"></i>
                      <strong>Standalone Mode:</strong> You can record your screen and audio without connecting to a meeting. 
                      This is great for creating tutorials, demos, or recording yourself.
                    </Alert>
                  )}
                  
                  <div className="text-center mb-4">
                    <div className={`p-4 rounded-circle d-inline-flex align-items-center justify-content-center mb-3 ${isRecording ? 'bg-danger' : 'bg-primary'}`} 
                      style={{ width: '120px', height: '120px', cursor: 'pointer' }}
                      onClick={toggleRecording}
                    >
                      <i className={`bi ${isRecording ? 'bi-pause-fill' : 'bi-record-fill'}`} style={{ fontSize: '3rem' }}></i>
                    </div>
                    
                    <h2 className="text-white mt-3 mb-1">
                      {formatTime(recordingTime)}
                    </h2>
                    <p className="text-white">
                      {isRecording ? 'Recording in progress' : 'Ready to record'}
                    </p>
                    
                    <Button 
                      variant={isRecording ? 'danger' : 'primary'} 
                      size="lg"
                      className="mt-3"
                      onClick={toggleRecording}
                    >
                      {isRecording ? 'Stop Recording' : 'Start Recording'}
                    </Button>
                  </div>
                  
                  <div className="d-flex justify-content-between mt-4">
                    <Button 
                      variant="outline-secondary" 
                      onClick={handleDisconnect}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
              
              {activeStep === 2 && (
                <div>
                  <h3 className="mb-4 text-white">Review Recording</h3>
                  
                  <Card className="glass-card mb-4">
                    <Card.Body>
                      <div className="d-flex align-items-center mb-3">
                        <div className="me-3 text-success" style={{ fontSize: '2rem' }}>
                          <i className="bi bi-check-circle-fill"></i>
                        </div>
                        <div>
                          <h5 className="mb-1 text-white">Recording Completed</h5>
                          <p className="mb-0 text-white">Duration: {formatTime(recordingTime)}</p>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                  
                  <div className="mb-4">
                    <h5 className="text-white mb-3">Preview</h5>
                    <div className="ratio ratio-16x9 mb-3 bg-dark">
                      {recordingData && (
                        recordingData.type === 'audio/webm' ? (
                          <audio 
                            src={recordingData.url} 
                            controls 
                            className="rounded"
                          />
                        ) : (
                          <video 
                            src={recordingData.url} 
                            controls 
                            className="rounded"
                          />
                        )
                      )}
                    </div>
                    <p className="text-white">
                      <small>
                        File size: {recordingData ? Math.round(recordingData.size / (1024 * 1024) * 10) / 10 : 0} MB
                      </small>
                    </p>
                  </div>
                  
                  <div className="d-flex justify-content-between mt-4">
                    <Button 
                      variant="outline-secondary" 
                      onClick={handleDisconnect}
                    >
                      Discard
                    </Button>
                    <Button 
                      variant="primary" 
                      onClick={handleSaveRecording}
                    >
                      Process Recording
                    </Button>
                  </div>
                </div>
              )}
              
              {activeStep === 3 && (
                <div>
                  <h3 className="mb-4 text-white">Processing Recording</h3>
                  
                  <Card className="glass-card mb-4">
                    <Card.Body className="text-center">
                      <h5 className="text-white mb-3">
                        {processingProgress < 50 ? 'Uploading Recording...' : 
                        processingProgress < 95 ? 'Transcribing and Summarizing...' : 
                        'Almost done!'}
                      </h5>
                      <ProgressBar animated now={processingProgress} className="mb-3" />
                      <p className="text-white mb-0">
                        {processingProgress < 100 ? 
                          "Please don't close this window. This may take a few minutes depending on the recording length." : 
                          "Redirecting to summary..."}
                      </p>
                    </Card.Body>
                  </Card>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default Record;
